// TSL text language — tokenizer + recursive-descent parser + canonical printer for EXPRESSIONS. The text
// surface compiles ONLY into the safe AST (src/engine/tslBlock/ast.ts): every identifier must be a known
// source / ctx field / declared param / state var, and every call must be a whitelisted function. Anything
// else (fetch, window, eval, arbitrary identifiers) is a parse error — there is no path from text to host
// code. printExpr() emits a fully-parenthesized canonical form, so parse(print(ast)) is byte-stable.

import { INDICATORS, SOURCES, type Expr } from './ast';

export interface ParseCtx { ctxFields: Set<string>; paramNames: Set<string>; stateNames: Set<string>; }
export type ParseResult = { ok: true; expr: Expr } | { ok: false; error: string; pos: number };

const FUNCS = new Set([...Object.keys(INDICATORS), 'prev', 'crossover', 'crossunder', 'min', 'max', 'abs', 'sign', 'nz']);
const SRC = new Set<string>(SOURCES);

// ── Deterministic parse budgets (fail closed with clear errors — never a JS stack overflow) ────────────
/** Max characters for one expression source. Generous: real expressions are tens of bytes. */
export const MAX_EXPR_TEXT = 65536;
/** Max identifier token length ('state.' prefix + a 64-char name fits comfortably). */
export const MAX_IDENT_TOKEN = 96;
/** Max grammar recursion depth. The recursive-descent cycle (ternary→…→primary→'('→ternary, plus the
 *  self-recursive `not`/unary chains) increments ONE shared counter, so pathological nesting like 10,000
 *  parens or `not not not …` errors deterministically long before the JS call stack is at risk. Valid
 *  corpus expressions nest a few dozen levels at most; the printer's fully-parenthesized canonical form
 *  adds no grammar depth beyond the AST's own nesting, which the validator caps at 300 nodes. */
export const MAX_PARSE_DEPTH = 512;

// ── tokenizer ──────────────────────────────────────────────────────────────────
type Tok = { k: 'num' | 'id' | 'op' | 'kw' | 'str'; v: string; pos: number };
const KEYWORDS = new Set(['and', 'or', 'not', 'true', 'false']);
function lex(s: string): Tok[] | { error: string; pos: number } {
  const out: Tok[] = []; let i = 0;
  const two = new Set(['>=', '<=', '==', '!=']);
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i++; continue; }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(s[i + 1] ?? ''))) {
      const number = s.slice(i).match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/)![0];
      out.push({ k: 'num', v: number, pos: i }); i += number.length; continue;
    }
    if (/[A-Za-z_]/.test(c)) { let j = i + 1; while (j < s.length && /[A-Za-z0-9_.]/.test(s[j])) j++; const v = s.slice(i, j); if (v.length > MAX_IDENT_TOKEN) return { error: `identifier too long (> ${MAX_IDENT_TOKEN} chars)`, pos: i }; out.push({ k: KEYWORDS.has(v) ? 'kw' : 'id', v, pos: i }); i = j; continue; }
    if (c === '"' || c === "'") { let j = i + 1; while (j < s.length && s[j] !== c) j++; if (j >= s.length) return { error: 'unterminated string', pos: i }; out.push({ k: 'str', v: s.slice(i + 1, j), pos: i }); i = j + 1; continue; }
    const t2 = s.slice(i, i + 2);
    if (two.has(t2)) { out.push({ k: 'op', v: t2, pos: i }); i += 2; continue; }
    if ('+-*/<>?:(),'.includes(c)) { out.push({ k: 'op', v: c, pos: i }); i++; continue; }
    return { error: `unexpected character '${c}'`, pos: i };
  }
  return out;
}

// ── parser (precedence: ?: < or < and < not < cmp < +- < */ < unary < primary) ──
export function parseExpr(text: string, ctx: ParseCtx): ParseResult {
  if (typeof text !== 'string' || text.length > MAX_EXPR_TEXT) return { ok: false, error: `expression too long (> ${MAX_EXPR_TEXT} chars)`, pos: 0 };
  const lexed = lex(text);
  if (!Array.isArray(lexed)) return { ok: false, error: lexed.error, pos: lexed.pos };
  const toks = lexed; let p = 0;
  const peek = () => toks[p];
  const eat = (v?: string) => { const t = toks[p]; if (v !== undefined && (!t || t.v !== v)) throw { error: `expected '${v}'`, pos: t?.pos ?? text.length }; p++; return t; };
  const isOp = (v: string) => peek()?.k === 'op' && peek().v === v;
  const isKw = (v: string) => peek()?.k === 'kw' && peek().v === v;

  // Shared depth counter across every self-recursive production (see MAX_PARSE_DEPTH).
  let depth = 0;
  const deeper = (): void => { if (++depth > MAX_PARSE_DEPTH) throw { error: `expression nesting too deep (> ${MAX_PARSE_DEPTH})`, pos: peek()?.pos ?? 0 }; };

  function ternary(): Expr { deeper(); try { const cond = orE(); if (isOp('?')) { eat('?'); const then = ternary(); eat(':'); const els = ternary(); return { t: 'cond', cond, then, else: els }; } return cond; } finally { depth--; } }
  function orE(): Expr { let a = andE(); while (isKw('or')) { eat(); a = { t: 'logic', op: 'or', a, b: andE() }; } return a; }
  function andE(): Expr { let a = notE(); while (isKw('and')) { eat(); a = { t: 'logic', op: 'and', a, b: notE() }; } return a; }
  function notE(): Expr { deeper(); try { if (isKw('not')) { eat(); return { t: 'unop', op: 'not', a: notE() }; } return cmpE(); } finally { depth--; } }
  function cmpE(): Expr { let a = addE(); while (peek()?.k === 'op' && ['>', '<', '>=', '<=', '==', '!='].includes(peek().v)) { const op = eat().v as Expr extends { t: 'cmp' } ? never : '>' | '<' | '>=' | '<=' | '==' | '!='; a = { t: 'cmp', op, a, b: addE() }; } return a; }
  function addE(): Expr { let a = mulE(); while (isOp('+') || isOp('-')) { const op = eat().v as '+' | '-'; a = { t: 'binop', op, a, b: mulE() }; } return a; }
  function mulE(): Expr { let a = unary(); while (isOp('*') || isOp('/')) { const op = eat().v as '*' | '/'; a = { t: 'binop', op, a, b: unary() }; } return a; }
  function unary(): Expr { deeper(); try { if (isOp('-')) { eat(); return { t: 'unop', op: 'neg', a: unary() }; } return primary(); } finally { depth--; } }

  function primary(): Expr {
    const t = peek();
    if (!t) throw { error: 'unexpected end of expression', pos: text.length };
    if (t.k === 'num') { eat(); const v = Number(t.v); if (!Number.isFinite(v)) throw { error: `bad number '${t.v}'`, pos: t.pos }; return { t: 'num', v }; }
    if (t.k === 'kw' && (t.v === 'true' || t.v === 'false')) { eat(); return { t: 'bool', v: t.v === 'true' }; }
    if (isOp('(')) { eat('('); const e = ternary(); eat(')'); return e; }
    if (t.k === 'id') {
      const name = t.v; eat();
      if (isOp('(')) return call(name, t.pos);
      if (SRC.has(name)) return { t: 'src', src: name as never };
      if (name.startsWith('state.')) { const sn = name.slice(6); if (!ctx.stateNames.has(sn)) throw { error: `unknown state var '${sn}'`, pos: t.pos }; return { t: 'state', name: sn }; }
      if (ctx.ctxFields.has(name)) return { t: 'ctx', field: name };
      if (ctx.paramNames.has(name)) return { t: 'param', name };
      throw { error: `unknown identifier '${name}' (not a source, parameter, state var, or context field)`, pos: t.pos };
    }
    throw { error: `unexpected token '${t.v}'`, pos: t.pos };
  }

  function args(): Expr[] { const a: Expr[] = []; eat('('); if (!isOp(')')) { a.push(ternary()); while (isOp(',')) { eat(','); a.push(ternary()); } } eat(')'); return a; }
  function call(name: string, pos: number): Expr {
    if (!FUNCS.has(name)) throw { error: `unknown function '${name}()' — not a whitelisted operation`, pos };
    const a = args();
    const arity = INDICATORS[name]?.arity ?? (['abs', 'sign', 'nz'].includes(name) ? 1 : 2);
    if (a.length !== arity) throw { error: `${name}() expects ${arity} arguments, got ${a.length}`, pos };
    if (name === 'prev') { const n = a[1]; if (!n || n.t !== 'num' || !Number.isInteger(n.v)) throw { error: 'prev(expr, n): n must be an integer literal', pos }; return { t: 'prev', expr: a[0], barsAgo: n.v }; }
    if (name === 'crossover' || name === 'crossunder') return { t: 'cross', dir: name === 'crossover' ? 'over' : 'under', a: a[0], b: a[1] };
    if (name === 'min' || name === 'max') return { t: 'binop', op: name, a: a[0], b: a[1] };
    if (name === 'abs' || name === 'sign' || name === 'nz') return { t: 'unop', op: name, a: a[0] };
    return { t: 'indicator', name, args: a };
  }

  try {
    const e = ternary();
    if (p < toks.length) return { ok: false, error: `unexpected trailing token '${peek().v}'`, pos: peek().pos };
    return { ok: true, expr: e };
  } catch (err) {
    const e = err as { error?: string; pos?: number };
    return { ok: false, error: e.error ?? String(err), pos: e.pos ?? 0 };
  }
}

// ── canonical printer (fully parenthesized → parse(print(ast)) is stable) ───────
export function printExpr(e: Expr): string {
  switch (e.t) {
    case 'num': return String(e.v);
    case 'bool': return e.v ? 'true' : 'false';
    case 'param': return e.name;
    case 'src': return e.src;
    case 'ctx': return e.field;
    case 'state': return `state.${e.name}`;
    case 'indicator': return `${e.name}(${e.args.map(printExpr).join(', ')})`;
    case 'prev': return `prev(${printExpr(e.expr)}, ${e.barsAgo})`;
    case 'unop': return e.op === 'neg' ? `(-${printExpr(e.a)})` : e.op === 'not' ? `(not ${printExpr(e.a)})` : `${e.op}(${printExpr(e.a)})`;
    case 'binop': return e.op === 'min' || e.op === 'max' ? `${e.op}(${printExpr(e.a)}, ${printExpr(e.b)})` : `(${printExpr(e.a)} ${e.op} ${printExpr(e.b)})`;
    case 'cmp': return `(${printExpr(e.a)} ${e.op} ${printExpr(e.b)})`;
    case 'logic': return `(${printExpr(e.a)} ${e.op} ${printExpr(e.b)})`;
    case 'cross': return `${e.dir === 'over' ? 'crossover' : 'crossunder'}(${printExpr(e.a)}, ${printExpr(e.b)})`;
    case 'cond': return `(${printExpr(e.cond)} ? ${printExpr(e.then)} : ${printExpr(e.else)})`;
    default: return '/* ? */';
  }
}
